import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import async from 'async';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import pluralize from 'pluralize';
import ContentSelector from '../../../components/ContentSelector/ContentSelector';
import Header from '../../../components/Header/Header';
import UploadPrompt from '../../../components/UploadPrompt/UploadPrompt';
import { addSupportedAttr, isUnixHiddenPath } from '../../../utils/file';
import errorMessage from '../../../utils/errorMessage';
import { getParentPath } from '../../../utils/parent';
import { SOLR_DELAY } from '../../../../constants';
import AddLocationDialog from '../../../components/Locations/AddLocationDialog';
import LocationSelector from '../../../components/Locations/LocationSelector';
import { addFolder, loadFolders, updateFolder, loadFolder } from '../../../actions/folderActions';
import {
  loadContentItems,
  contentItemsUnselectAll,
  fetchContentItems,
  loadAllContentItems,
} from '../../../actions/contentItemActions';
import { loadPlaylists, loadPlaylist, updatePlaylist } from '../../../actions/playlistActions';
import {
  getAssets,
  uploadAsset,
  queueUploads,
  getEncodingQueue,
  uploadRemoveItem,
  updateAsset,
  getAsset,
  getEncodeTypes,
} from '../../../actions/assetActions';
import { toggleFilters, closeFilters } from '../../../actions/filterActions';
import { getFolderUrl } from '../../../utils/v4/utils';
import Pagination from '../../Pagination/Pagination';
import qs from '../../../utils/qs';
import ContentSearch from '../../../components/ContentSearch/ContentSearch';
import ContentTable from '../../../components/ContentsTable/ContentsTable';
import ContentFilters from '../../../components/ContentFilters/ContentFilters';
import ConfirmModal from '../../../components/ConfirmActions/ConfirmActions';
import Confirm from '../../../components/Confirm/Confirm';
import { getSigns, updateSign, getSign } from '../../../actions/signActions';
import { addNotification } from '../../../actions/notificationActions';
import { updateCampaign, loadCampaign } from '../../../actions/campaignActions';
import {
  deleteItemsHelper,
  getFilteredItemsForDeletionHelper,
  getMergedItems,
  mGetItemsForDeletionHelper,
  prepareDeleteFunctionHelper,
  showFailedDeleteWarningHelper,
  getUploadingFolders,
  getConflictFolderName,
  createFolderForItems,
  getSelectedFolders,
  calculateNewSelection,
  getMoveAction,
  getDeleteFolderComponent,
} from '../../../components/ContentHelper/ContentHelper';
import { loadSequences, loadSequence, updateSequence } from '../../../actions/sequenceActions';
import { updateSchedule, loadSchedule } from '../../../actions/scheduleActions';
import { getCampaignSchedule } from '../../../actions/campaignScheduleActions';
import AssetsUploads from '../../../components/Assets/AssetsUploads';
import Spinner from '../../../components/Spinner/Spinner';
import { getPermissions } from '../../../utils/getPermissions';
import ContentActions from './ContentActions';
import DeleteConfirmation from '../../../components/ContentItems/DeleteConfirmation';
import FiltersApplied, { shouldShowFiltersApplied } from '../../FiltersApplied/FiltersApplied';
import urlHelper from '../../../utils/urlHelper';
import { getUsers } from '../../../actions/userActions';
import { buildUrlForRemoveQueryParam } from '../../../utils/updateQueryString';

// Use "globalThis" instead of "window", "self", or "global" (javascript:S7764)
const window = globalThis.window;

/**
 * @typedef {Object} ContentItem
 * @property {string|number} [id] - UI or API identifier for the item.
 * @property {string|number} [content_id] - Content identifier used by most content APIs.
 * @property {string} [fileId] - Asset identifier for image, video, and file types.
 * @property {'folder'|'image'|'video'|'file'|'playlist'|'sign'|'sequence'|'schedule'|'campaign'|string} type - Content type used to decide navigation and actions.
 * @property {string} name - Display name shown in the UI.
 * @property {string} [parent] - Parent folder path or identifier.
 * @property {string[]} [tags] - Item tags.
 */

/**
 * @typedef {Object.<number, ContentItem>} SelectedContentMap
 */

/**
 * @typedef {Object} FolderTarget
 * @property {string|number} id - Destination folder identifier.
 * @property {string} name - Destination folder name.
 * @property {string} parent - Destination folder parent path.
 */

/**
 * @typedef {Object} DeletionFilterResult
 * @property {ContentItem[]} itemsToDelete - Items that can be deleted immediately.
 * @property {ContentItem[]} foldersInUse - Folder items blocked by sign usage.
 * @property {ContentItem[]} imagesInUse - Image items blocked by sign usage.
 * @property {ContentItem[]} playlistsInUse - Playlist items blocked by sign usage.
 * @property {Array<{ campaignId: string|number, campaignName: string }>} campaignsInUse - Campaigns blocked by schedule usage.
 * @property {ContentItem[]} videosInUse - Video items blocked by playlist usage.
 */

/**
 * @typedef {Object} ContentPageProps
 * @property {{ push: (url: string) => void, replace: (url: string) => void, location: { pathname: string } }} [history] - Router history used for navigation and query updates.
 * @property {{ search: string }} location - Current router location data.
 * @property {{ contentItems?: { content_items?: ContentItem[], numFound?: number }, loadingContentItems?: boolean, selected?: SelectedContentMap }} contentItems - Content state for the current page.
 * @property {{ applied?: Record<string, any> }} [filter] - Active filter state.
 * @property {object} auth - Auth state used for permission checks.
 * @property {object[]} users - User records used in applied filter summaries.
 * @property {{ folders: { folders: Array<FolderTarget & { parent: string }> } }} folders - Folder state used by move and create dialogs.
 * @property {{ uploading: object[], encodingQueue: object[], encodeTypes?: object[], assets?: { assets: ContentItem[] } }} assets - Asset upload and lookup state.
 * @property {{ playlists: { playlists: object[] } }} playlists - Playlist lookup state.
 * @property {{ sequences: { sequences: object[] } }} sequences - Sequence lookup state.
 * @property {{ campaignSchedule: { campaignScheduleEntries: object[] } }} campaignSchedule - Campaign schedule lookup state.
 * @property {{ signs: { signs: object[] } }} signs - Sign lookup state.
 * @property {(query: string) => *} loadPlaylists - Loads playlists for the content page.
 * @property {(query: string) => *} getAssets - Loads assets for the content page.
 * @property {() => *} getCampaignSchedule - Loads campaign schedule data.
 * @property {(query: string) => *} getSigns - Loads signs for the content page.
 * @property {(query: string) => *} loadSequences - Loads sequences for the content page.
 * @property {(query: string) => *} loadContentItems - Loads the visible content items for the page.
 * @property {(files: Array<File>) => *} queueUploads - Adds files to the upload queue.
 * @property {() => void} toggleFilters - Toggles the filter panel.
 * @property {(notification: { type: string, text: string }) => void} addNotification - Displays page notifications.
 * @property {() => *} contentItemsUnselectAll - Clears the shared content selection state.
 * @property {(query: string) => *} loadFolders - Loads folders used by the page and dialogs.
 * @property {() => *} loadAllContentItems - Loads the complete content dataset used by background actions.
 * @property {(file: File & object, next: Function) => *} uploadAsset - Uploads a single asset and optionally continues the queue.
 * @property {() => *} getEncodingQueue - Refreshes the encoding queue state.
 * @property {(id: string|number) => *} uploadRemoveItem - Removes a queued upload item.
 * @property {(id: string|number) => Promise<{ result: object }>} loadFolder - Loads a folder for move operations.
 * @property {(id: string|number) => Promise<{ result: object }>} loadCampaign - Loads a campaign for move operations.
 * @property {(id: string) => Promise<{ result: object }>} getAsset - Loads an asset for move operations.
 * @property {(id: string|number) => Promise<{ result: object }>} loadPlaylist - Loads a playlist for move operations.
 * @property {(id: string|number) => Promise<{ result: object }>} getSign - Loads a sign for move operations.
 * @property {(id: string|number) => Promise<{ result: object }>} loadSequence - Loads a sequence for move operations.
 * @property {(id: string|number) => Promise<{ result: object }>} loadSchedule - Loads a schedule for move operations.
 * @property {() => *} getEncodeTypes - Loads available encoding presets.
 * @property {() => *} getUsers - Loads users used by filter metadata.
 * @property {() => void} closeFilters - Closes the filter panel when the page unmounts.
 */

/**
 * Renders the v4 content page and coordinates uploads, search, selection, deletion, and move actions.
 * @param {ContentPageProps} props - Data and action creators required by the content page.
 * @returns {JSX.Element} The rendered content management page.
 */
export function Content(props) {
  const { contentItems = {}, filter = {} } = props;
  const { contentItems: queryContentItems = {}, loadingContentItems } = contentItems;
  const { content_items: queryArrOfContentItems = [], numFound } = queryContentItems;
  const { loadContentItems, toggleFilters } = props;
  const itemsToDisplay = queryArrOfContentItems;
  const refWarning = useRef();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalOnClick, setConfirmModalOnClick] = useState(() => () => {});
  const [confirmModalSubtitle, setConfirmModalSubtitle] = useState('');
  const [confirmModalBtnText, setConfirmModalBtnText] = useState('');
  const [deepDelete, setDeepDelete] = useState(false);
  const [deleteFolderComponent, setDeleteFolderComponent] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState({});
  const [shiftLastIndex, setShiftLastIndex] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFolderSelector, setShowFolderSelector] = useState(false);

  /**
   * Enriches the current content list with sequence and playlist entry data and applies tier filtering.
   * @param {ContentItem[]} items - Raw content items returned by the current query.
   * @returns {ContentItem[]} Items prepared for display on the page.
   */
  const mergeData = items => {
    const {
      auth,
      sequences: {
        sequences: { sequences },
      },
      playlists: {
        playlists: { playlists },
      },
    } = props;
    const permissions = getPermissions(auth);

    return getMergedItems({ items, sequences, playlists, permissions });
  };

  const itemsToDisplayForTier = mergeData(itemsToDisplay);
  const {
    assets: { encodeTypes },
  } = props;
  const {
    loadPlaylists,
    getAssets,
    getSigns,
    loadFolders,
    loadSequences,
    getCampaignSchedule,
    getEncodeTypes,
    getUsers,
    location: { search },
    signs: {
      signs: { signs },
    },
  } = props;
  const { assets, uploadRemoveItem, getEncodingQueue } = props;
  const { uploading, encodingQueue } = assets;

  /**
   * Loads the supporting datasets required by the page on mount and closes the filter panel on unmount.
   * @returns {(() => void)} Cleanup callback that closes the filter panel.
   */
  useEffect(() => {
    const { closeFilters } = props;

    getCampaignSchedule();
    loadPlaylists('?count=100000000');
    getAssets('?count=100000000');
    getSigns('?count=100000000');
    loadFolders('?count=100000000');
    loadSequences('?count=100000000');
    getUsers();
    getEncodeTypes();

    return () => {
      closeFilters();
    };
  }, []);
  let encodeType = {};
  /**
   * Closes the upload prompt dialog.
   * @returns {void}
   */
  const hideUploadDialog = () => setShowUploadDialog(false);

  /**
   * Checks whether any folders implied by an upload already exist under the current destination path.
   * @param {string[]} uploadingFolders - Folder paths derived from the selected upload files.
   * @returns {string[]} Conflicting root folder names.
   */
  const getConflictedUploadingFolders = uploadingFolders => {
    const {
      folders: {
        folders: { folders },
      },
    } = props;
    const parentPath = getParentPath(props);
    const rootFolders = [];

    return getConflictFolderName({ uploadingFolders, rootFolders, folders, parentPath });
  };

  /**
   * Removes unsupported or hidden files before starting the upload flow.
   * @param {Array<File & { webkitRelativePath?: string }>} allFiles - Files selected for upload.
   * @returns {Array<File & { webkitRelativePath?: string }>} Uploadable files with supported-type metadata applied.
   */
  const filterUploadingFiles = allFiles =>
    addSupportedAttr(allFiles.filter(item => !isUnixHiddenPath(item.webkitRelativePath)));

  /**
   * Creates any required upload folders before queued files are uploaded.
   * @param {string[]} items - Remaining folder paths that still need to be created.
   * @param {Array<File>} files - Files waiting to be uploaded.
   * @returns {Promise<void>} Resolves after the next folder step is handled.
   */
  const addUploadingFolders = (items, files) => {
    const {
      location: { search },
      loadFolders,
      loadContentItems,
      addNotification,
    } = props;
    const parentPath = getParentPath(props);

    return createFolderForItems({
      items,
      loadFolders,
      loadContentItems,
      search,
      uploadStart,
      files,
      parentPath,
      addFolder,
      addUploadingFolders,
      addNotification,
      errorMessage,
    });
  };

  /**
   * Starts the next upload in the global upload queue when capacity is available.
   * @returns {void}
   */
  const uploadNext = () => {
    const { uploadAsset } = props;

    if (!window.SignStix.uploadingFiles.length) {
      return;
    }

    const file = window.SignStix.uploadingFiles.shift();

    uploadAsset(file, uploadNext);
  };

  /**
   * Normalizes selected files, adds them to the shared upload queue, and kicks off concurrent uploads.
   * @param {Array<File & { webkitRelativePath?: string, id?: number, parent?: string, encodeType?: object }>} _files - Files selected for upload.
   * @returns {void}
   */
  const uploadStart = _files => {
    const { queueUploads } = props;

    if (!_files.length) {
      return;
    }

    const files = _files.map(file => {
      file.id = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
      file.parent =
        getParentPath(props) + (file.webkitRelativePath || '').slice(0, -file.name.length);
      file.encodeType = encodeType;

      return file;
    });

    window.SignStix.uploadingFiles.push(...files);
    queueUploads(files);
    const limit = Math.min(window.SignStix.uploadingFiles.length, window.SignStix.uploadingLimit);

    while (window.SignStix.uploading < limit) {
      uploadNext();
    }
  };

  /**
   * Validates upload inputs, checks for folder conflicts, and begins the upload flow.
   * @param {Array<File & { webkitRelativePath?: string }>} allFiles - Files selected in the upload prompt.
   * @param {object} _encodeType - Encoding preset selected for the upload batch.
   * @returns {void}
   */
  const uploadAssets = (allFiles, _encodeType) => {
    const { addNotification } = props;
    const files = filterUploadingFiles(allFiles);
    const folders = getUploadingFolders(files);
    const conflicts = getConflictedUploadingFolders(folders);

    hideUploadDialog();

    if (conflicts.length) {
      addNotification({
        type: 'danger',
        text: `Folders exist: ${conflicts.join(', ')}`,
      });

      return;
    }

    encodeType = _encodeType;
    addUploadingFolders(folders, files);
  };

  /**
   * Reloads the visible content items whenever the current search query changes.
   * @returns {void}
   */
  useEffect(() => {
    if (search) {
      loadContentItems(search);
    } else {
      loadContentItems('?parent=/*&sort=createdDate+desc&count=25');
    }
  }, [search]);

  /**
   * Opens the selected content item, or the folder edit page when requested.
   * @param {ContentItem} item - Item that should be opened.
   * @param {boolean} [isFolderEdit] - Whether the action should open the folder editor directly.
   * @returns {void}
   */
  const onLatestUploadItemClick = (item, isFolderEdit) => {
    const { history } = props;

    setSelectedContent({});

    if (isFolderEdit) {
      history.push(`/${item.type}s/${item.content_id}`);

      return;
    }

    let url = item.type === 'folder' ? getFolderUrl(item, props) : urlHelper.getContentUrl(item);

    if (item.type === 'folder') {
      const location = new URL(url, window.location.origin);

      url = buildUrlForRemoveQueryParam(location, 'type', 'folder');
    }

    history.push(url.replace('home', 'content'));
  };

  /**
   * Redirects page to include search query param
   * @param {string} searchValue
   */
  const executeSearch = searchValue => {
    const { history, location } = props;

    history.push(urlHelper.setUrlParam(location.search, 'search', searchValue.trim()));
  };

  /**
   * Deletes the provided content items and then clears the local row selection.
   * @param {ContentItem[]} contentItems - Items that should be deleted.
   * @returns {void}
   */
  const deleteItems = contentItems => {
    const {
      location: { search },
      addNotification,
      contentItemsUnselectAll,
      loadFolders,
      loadContentItems,
      loadAllContentItems,
    } = props;

    deleteItemsHelper({
      loadFolders,
      addNotification,
      contentItemsUnselectAll,
      loadContentItems,
      loadAllContentItems,
      contentItems,
      search,
      setDeepDelete,
    });
    setSelectedContent({});
  };

  /**
   * Shows a warning for any items that are blocked from deletion.
   * @param {DeletionFilterResult} items - Grouped deletion results.
   * @returns {void}
   */
  const showFailedDeleteWarning = items => {
    showFailedDeleteWarningHelper({
      items,
      refWarning,
    });
  };

  /**
   * Filters deletion candidates into allowed and blocked groups.
   * @param {ContentItem[]} items - Candidate items for deletion.
   * @returns {DeletionFilterResult} Grouped deletion results.
   */
  const getFilteredItemsForDeletion = items => {
    const { folders, assets, playlists, campaignSchedule } = props;

    return getFilteredItemsForDeletionHelper({
      items,
      folders,
      assets,
      playlists,
      campaignSchedule,
    });
  };

  /**
   * Expands a selection into the concrete list of items that should be deleted.
   * @param {{ content_items: ContentItem[] }} allContentItems - Full content dataset fetched for deletion.
   * @param {ContentItem[]} selectedItems - Selected items targeted for deletion.
   * @param {boolean} deleteAllContent - Whether folder contents should also be deleted.
   * @returns {ContentItem[]} Items that should be processed by the delete workflow.
   */
  const mGetItemsForDeletion = (allContentItems, selectedItems, deleteAllContent) => {
    const {
      contentItems: { contentItems },
    } = props;

    return mGetItemsForDeletionHelper({
      allContentItems,
      selectedItems,
      deleteAllContent,
      contentItems,
    });
  };

  /**
   * Returns the folders from the current selection.
   * @returns {ContentItem[]} Selected folder items.
   */
  const selectedFolders = () => {
    const { contentItems } = props;

    return getSelectedFolders(itemsToDisplayForTier, contentItems.selected);
  };

  const DeleteFolderContents = getDeleteFolderComponent(setDeepDelete, deepDelete);

  /**
   * Configures the delete confirmation modal for a single content item.
   * @param {{ title: string, subtitle: string, content: ContentItem }} params - Delete modal copy and target item.
   * @returns {void}
   */
  const prepareDeleteFunction = ({ title, subtitle, content }) => {
    prepareDeleteFunctionHelper({
      title,
      subtitle,
      content,
      setConfirmModalTitle,
      setConfirmModalSubtitle,
      setConfirmModalOnClick,
      fetchContentItems,
      setDeleteFolderComponent,
      setConfirmModalBtnText,
      setShowConfirmModal,
      mGetItemsForDeletion,
      getFilteredItemsForDeletion,
      showFailedDeleteWarning,
      deleteItems,
    });
  };

  /**
   * Creates a folder under the current parent path and refreshes the visible content.
   * @param {{ name: string, parent?: string }} folder - Folder payload from the create dialog.
   * @returns {void}
   */
  const handleAddFolder = folder => {
    const {
      location: { search },
      addNotification,
      loadFolders,
      loadContentItems,
    } = props;

    setShowFolderDialog(false);
    folder.parent = getParentPath(props);
    addFolder(folder).then(
      () => {
        addNotification({ type: 'success', text: 'Folder created.' });
        loadFolders('?parent=/&sort=createdDate+desc&count=10');
        loadContentItems(search);
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  /**
   * Counts how many rows are currently selected in the table.
   * @returns {number} Number of selected rows.
   */
  const selectedLength = () => Object.keys(selectedContent).length;

  /**
   * Builds the move request for a single selected content item.
   * @param {ContentItem} item - Item being moved.
   * @param {FolderTarget} parent - Destination folder.
   * @returns {Promise<*>} Promise that resolves when the move request completes.
   */
  const moveItem = (item, parent) => {
    const {
      getAsset,
      loadPlaylist,
      getSign,
      loadSequence,
      loadSchedule,
      loadCampaign,
      loadFolder,
    } = props;

    return getMoveAction(item, parent, {
      getAsset,
      loadPlaylist,
      updateAsset,
      updatePlaylist,
      getSign,
      updateSign,
      loadSequence,
      updateSequence,
      loadSchedule,
      updateSchedule,
      loadCampaign,
      updateCampaign,
      loadFolder,
      updateFolder,
    });
  };

  /**
   * Moves all selected items into the chosen destination folder and refreshes page data afterwards.
   * @param {FolderTarget} parent - Destination folder selected in the move dialog.
   * @returns {void}
   */
  const moveItems = parent => {
    const {
      location: { search },

      addNotification,
      contentItemsUnselectAll,
      loadFolders,
      loadAllContentItems,
      loadContentItems,
    } = props;

    setShowFolderSelector(false);
    const data = itemsToDisplayForTier;
    const holdingMessage = `Moving ${selectedLength()} ${pluralize('Item', selectedLength())}`;

    addNotification({ type: 'info', text: holdingMessage });

    let errorCount = 0;

    async.eachSeries(
      Object.keys(selectedContent),
      (key, callback) => {
        moveItem(data[key], parent).then(
          () => callback(),
          () => {
            errorCount += 1;
            callback();
          },
        );
      },
      () => {
        if (errorCount > 0) {
          const failureMessage = `${errorCount} ${pluralize(
            'Item',
            errorCount,
          )} out of ${selectedLength()} failed to move`;

          addNotification({ type: 'error', text: failureMessage });
        } else {
          const successMessage = `${pluralize('Item', selectedLength(), true)} moved`;

          addNotification({ type: 'success', text: successMessage });
        }

        contentItemsUnselectAll();
        setTimeout(() => {
          loadFolders('?count=100000000');
          loadAllContentItems();
          loadContentItems(search);
          setSelectedContent({});
        }, SOLR_DELAY);
      },
    );
  };

  /**
   * Updates the local selection map in response to a row click.
   * @param {{ shiftKey: boolean, ctrlKey: boolean, metaKey: boolean }} event - Click event modifier state.
   * @param {ContentItem} content - Item associated with the clicked row.
   * @param {number} index - Row index in the current table view.
   * @returns {void}
   */

  const handleContentSelectedChange = (event, content, index) => {
    window.getSelection().removeAllRanges();

    const { newSelection, newShiftIndex } = calculateNewSelection({
      event: {
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
      },
      index,
      content,
      selectedContent,
      shiftLastIndex,
      itemsToDisplay: itemsToDisplayForTier,
    });

    setSelectedContent(newSelection);
    setShiftLastIndex(newShiftIndex);
  };

  const { folders, location } = props;

  /**
   * Deletes the currently selected items after expanding folder contents and filtering blocked items.
   * @returns {void}
   */
  const onDelete = () => {
    setShowDeleteDialog(false);
    fetchContentItems('?count=100000000').then(res => {
      const itemsForDeletion = mGetItemsForDeletion(
        res,
        Object.values(selectedContent),
        deepDelete,
      );
      const items = getFilteredItemsForDeletion(itemsForDeletion);

      showFailedDeleteWarning(items);
      deleteItems(items.itemsToDelete);
    });
  };

  const foldersBeingMoved = getSelectedFolders(itemsToDisplayForTier, selectedContent);
  const { applied: filtersApplied = [] } = filter || {};
  const filterAppliedKeys = Object.keys(filtersApplied);
  const { users } = props;
  const itemType = qs(props).type;
  const SELECTOR_TYPES = [
    'sign',
    'video',
    'image',
    'playlist',
    'folder',
    'sequence',
    'schedule',
    'campaign',
  ];
  const hasNonSearchNonTypeFilters = filterAppliedKeys.some(k => k !== 'type' && k !== 'search');
  const showFiltersApplied =
    shouldShowFiltersApplied(filter) &&
    (hasNonSearchNonTypeFilters || (!!itemType && !SELECTOR_TYPES.includes(itemType)));

  return (
    <div className="bg-[#EFF5F8] min-h-screen content-page-container">
      <Helmet title="Content" />
      <ContentFilters />
      <div data-test="page-content" className="page-content !bg-transparent">
        <Header />
        <AssetsUploads
          uploading={uploading}
          uploadRemoveItem={uploadRemoveItem}
          encodingQueue={encodingQueue}
          getEncodingQueue={getEncodingQueue}
        />
        {showFiltersApplied && (
          <div className="toolbar">
            <FiltersApplied users={users} />
          </div>
        )}
        {/* CONTENT PICKER COMPONENT */}
        <ContentSelector
          setShowFolderDialog={setShowFolderDialog}
          creatorProps={props}
          setShowUploadDialog={setShowUploadDialog}
          folders={folders}
          location={location}
        />
        <ContentActions
          selectedItemsLength={Object.keys(selectedContent).length}
          onClickDelete={() => setShowDeleteDialog(true)}
          showFolderSelector={() => setShowFolderSelector(true)}
        />
        {/* /SEARCH COMPONENT */}
        <ContentSearch toggleFilters={toggleFilters} executeSearch={executeSearch} />
        {/* UPLOADS COMPONENT */}
        <Spinner loading={loadingContentItems}>
          <ContentTable
            itemsToDisplay={itemsToDisplayForTier}
            onLatestUploadItemClick={onLatestUploadItemClick}
            prepareDeleteFunction={prepareDeleteFunction}
            handleContentSelectedChange={handleContentSelectedChange}
            selectedContent={selectedContent}
            signs={signs}
            itemType={itemType}
          />
          <Pagination itemsFound={numFound} />
        </Spinner>
        <Confirm ref={refWarning} />
        <ConfirmModal
          show={showConfirmModal}
          title={confirmModalTitle}
          onClick={() => confirmModalOnClick(deepDelete)}
          subtitle={deleteFolderComponent ? '' : confirmModalSubtitle}
          setShowConfirmModal={setShowConfirmModal}
          data={[]}
          confirmModalBtnText={confirmModalBtnText}
          additionalComponent={deleteFolderComponent ? DeleteFolderContents : null}
          deepDelete={deepDelete}
          onCancelClick={() => {
            setDeleteFolderComponent(null);
            setDeepDelete(false);
          }}
        />
        <UploadPrompt
          show={showUploadDialog}
          onHide={hideUploadDialog}
          title="Upload Assets"
          encodeTypes={encodeTypes}
          onSuccess={uploadAssets}
          multiple
        />
        <DeleteConfirmation
          show={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onSuccess={onDelete}
          toggleDeepDelete={() => setDeepDelete(!deepDelete)}
          deepDelete={deepDelete}
          itemLength={Object.keys(selectedContent).length}
          foldersSelected={selectedFolders().length}
        />
        <LocationSelector
          type="folder"
          show={showFolderSelector}
          onHide={() => setShowFolderSelector(false)}
          onSuccess={moveItems}
          items={folders.folders.folders}
          moving={foldersBeingMoved}
        />
        <AddLocationDialog
          show={showFolderDialog}
          onHide={() => setShowFolderDialog(false)}
          type="folder"
          onSuccess={handleAddFolder}
          locations={[]}
          parent=""
        />
      </div>
    </div>
  );
}

Content.propTypes = {
  history: PropTypes.object,
  loadPlaylists: PropTypes.func.isRequired,
  getAssets: PropTypes.func.isRequired,
  getCampaignSchedule: PropTypes.func.isRequired,
  getSigns: PropTypes.func.isRequired,
  loadSequences: PropTypes.func.isRequired,
  contentItems: PropTypes.object.isRequired,
  loadContentItems: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  queueUploads: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  folders: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired,
  playlists: PropTypes.object.isRequired,
  addNotification: PropTypes.func.isRequired,
  contentItemsUnselectAll: PropTypes.func.isRequired,
  loadFolders: PropTypes.func.isRequired,
  loadAllContentItems: PropTypes.func.isRequired,
  campaignSchedule: PropTypes.object.isRequired,
  uploadAsset: PropTypes.func.isRequired,
  getEncodingQueue: PropTypes.func.isRequired,
  uploadRemoveItem: PropTypes.func.isRequired,
  loadFolder: PropTypes.func.isRequired,
  loadCampaign: PropTypes.func.isRequired,
  getAsset: PropTypes.func.isRequired,
  loadPlaylist: PropTypes.func.isRequired,
  getSign: PropTypes.func.isRequired,
  loadSequence: PropTypes.func.isRequired,
  loadSchedule: PropTypes.func.isRequired,
  signs: PropTypes.object.isRequired,
  filter: PropTypes.object,
  sequences: PropTypes.object.isRequired,
  getEncodeTypes: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  closeFilters: PropTypes.func.isRequired,
};

/**
 * Maps the Redux state slices required by the content page into component props.
 * @param {Object} state - Redux application state.
 * @returns {{ contentItems: object, filter: object, loadContentItems: *, auth: object, users: object[], folders: object, assets: object, playlists: object, sequences: object, campaignSchedule: object, signs: object }} Connected props derived from Redux state.
 */
const mapStateToProps = state => ({
  contentItems: state.contentItems,
  filter: state.filter,
  loadContentItems: PropTypes.func.isRequired,
  auth: state.auth,
  users: state.users.users,
  folders: state.folders,
  assets: state.assets,
  playlists: state.playlists,
  sequences: state.sequences,
  campaignSchedule: state.campaignSchedule,
  signs: state.signs,
});
const mapDispatchToProps = {
  loadContentItems,
  toggleFilters,
  queueUploads,
  getSigns,
  loadAllContentItems,
  uploadAsset,
  contentItemsUnselectAll,
  loadFolders,
  addNotification,
  loadPlaylists,
  getCampaignSchedule,
  loadSequences,
  getAssets,
  getEncodingQueue,
  uploadRemoveItem,
  loadFolder,
  loadCampaign,
  getSign,
  getAsset,
  loadPlaylist,
  loadSequence,
  loadSchedule,
  getEncodeTypes,
  getUsers,
  closeFilters,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Content));
